import { Select, SelectContent, SelectItem } from "@aix/ui"
import heic2any from "heic2any"
import React, { useEffect, useState } from "react"
import toast, { Toaster } from "react-hot-toast"

interface ConvertedFile {
  id: string
  originalName: string
  originalSize: number
  convertedBlob: Blob
  convertedUrl: string
  status: "pending" | "converting" | "completed" | "error"
  errorMessage?: string
}

type InputFormat = "heic" | "jpeg" | "png" | "webp" | "avif" | "bmp" | "tiff"
type OutputFormat = "jpeg" | "png" | "webp"

interface FormatOption {
  value: InputFormat | OutputFormat
  label: string
  available: boolean
  comingSoon?: boolean
}

const INPUT_FORMATS: FormatOption[] = [
  { value: "heic", label: "HEIC/HEIF", available: true },
  { value: "jpeg", label: "JPEG", available: true },
  { value: "png", label: "PNG", available: true },
  { value: "webp", label: "WebP", available: true },
  { value: "avif", label: "AVIF", available: false, comingSoon: true },
  { value: "bmp", label: "BMP", available: true },
  { value: "tiff", label: "TIFF", available: false, comingSoon: true },
]

const OUTPUT_FORMATS: FormatOption[] = [
  { value: "jpeg", label: "JPEG", available: true },
  { value: "png", label: "PNG", available: true },
  { value: "webp", label: "WebP", available: true },
]

/**
 * Image Converter - Convert between different image formats
 *
 * Features:
 * - Upload up to 100 images at once
 * - Batch conversion (5 files at a time)
 * - Multiple input/output format support
 * - Individual file download
 * - Bulk download all converted files
 * - Progress tracking
 */
function ImageConverter() {
  const [files, setFiles] = useState<ConvertedFile[]>([])
  const [isConverting, setIsConverting] = useState(false)
  const [currentBatch, setCurrentBatch] = useState(0)
  const [totalBatches, setTotalBatches] = useState(0)
  const [inputFormat, setInputFormat] = useState<InputFormat>("heic")
  const [outputFormat, setOutputFormat] = useState<OutputFormat>("jpeg")
  const [quality, setQuality] = useState(0.9)
  const [optimizeSize, setOptimizeSize] = useState(false)

  const BATCH_SIZE = 5
  const MAX_FILES = 100

  const getFileAcceptString = () => {
    switch (inputFormat) {
      case "heic":
        return ".heic,.heif,image/heic,image/heif"
      case "jpeg":
        return ".jpg,.jpeg,image/jpeg"
      case "png":
        return ".png,image/png"
      case "webp":
        return ".webp,image/webp"
      case "avif":
        return ".avif,image/avif"
      case "bmp":
        return ".bmp,image/bmp"
      case "tiff":
        return ".tiff,.tif,image/tiff"
      default:
        return ".heic,.heif,image/heic,image/heif"
    }
  }

  const getFileExtension = () => {
    switch (outputFormat) {
      case "jpeg":
        return "jpg"
      case "png":
        return "png"
      case "webp":
        return "webp"
      default:
        return "jpg"
    }
  }

  const validateFileFormat = (file: File): boolean => {
    switch (inputFormat) {
      case "heic":
        return (
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif")
        )
      case "jpeg":
        return (
          file.type === "image/jpeg" ||
          file.name.toLowerCase().endsWith(".jpg") ||
          file.name.toLowerCase().endsWith(".jpeg")
        )
      case "png":
        return file.type === "image/png" || file.name.toLowerCase().endsWith(".png")
      case "webp":
        return file.type === "image/webp" || file.name.toLowerCase().endsWith(".webp")
      case "avif":
        return file.type === "image/avif" || file.name.toLowerCase().endsWith(".avif")
      case "bmp":
        return file.type === "image/bmp" || file.name.toLowerCase().endsWith(".bmp")
      case "tiff":
        return (
          file.type === "image/tiff" ||
          file.name.toLowerCase().endsWith(".tiff") ||
          file.name.toLowerCase().endsWith(".tif")
        )
      default:
        return false
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])

    if (selectedFiles.length === 0) return

    // Check total file limit
    if (files.length + selectedFiles.length > MAX_FILES) {
      toast.error(
        `Maximum ${MAX_FILES} files allowed. You're trying to add ${selectedFiles.length} files to existing ${files.length} files.`,
      )
      return
    }

    // Filter and validate files based on selected input format
    const validFiles = selectedFiles.filter((file) => validateFileFormat(file))

    if (validFiles.length === 0) {
      const formatLabel = INPUT_FORMATS.find((f) => f.value === inputFormat)?.label || inputFormat.toUpperCase()
      toast.error(`No valid ${formatLabel} files found. Please select ${formatLabel} files.`)
      return
    }

    if (validFiles.length !== selectedFiles.length) {
      toast(`${selectedFiles.length - validFiles.length} invalid files were skipped.`, {
        icon: "⚠️",
        duration: 4000,
      })
    }

    // Create ConvertedFile objects
    const newFiles: ConvertedFile[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      originalName: file.name,
      originalSize: file.size,
      convertedBlob: file as any, // Will be replaced during conversion
      convertedUrl: "",
      status: "pending",
    }))

    setFiles((prev) => [...prev, ...newFiles])
    const formatLabel = INPUT_FORMATS.find((f) => f.value === inputFormat)?.label || inputFormat.toUpperCase()
    toast.success(`${validFiles.length} ${formatLabel} files added for conversion.`)
  }

  const convertSingleFile = async (file: File): Promise<Blob> => {
    // HEIC conversion using heic2any library
    if (inputFormat === "heic") {
      const outputQuality = optimizeSize ? Math.min(quality, 0.8) : quality
      const convertResult = await heic2any({
        blob: file,
        toType: `image/${outputFormat}`,
        quality: outputQuality,
      })

      return Array.isArray(convertResult) ? convertResult[0] : convertResult
    }

    // Canvas-based conversion for all other supported formats
    if (["jpeg", "png", "webp", "bmp"].includes(inputFormat)) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext("2d")

            if (!ctx) {
              reject(new Error("Could not get canvas context"))
              return
            }

            // Optional size optimization for large images
            let { width, height } = img
            if (optimizeSize && (width > 2048 || height > 2048)) {
              const scale = Math.min(2048 / width, 2048 / height)
              width = Math.floor(width * scale)
              height = Math.floor(height * scale)
            }

            canvas.width = width
            canvas.height = height
            ctx.drawImage(img, 0, 0, width, height)

            // Determine quality based on output format and settings
            let outputQuality = quality
            if (optimizeSize) {
              outputQuality = Math.min(quality, 0.8)
            }

            // PNG doesn't use quality parameter, only JPEG and WebP do
            const qualityParam = outputFormat === "png" ? undefined : outputQuality

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error("Failed to convert image"))
                }
              },
              `image/${outputFormat}`,
              qualityParam,
            )
          } catch (error) {
            reject(error)
          }
        }

        img.onerror = () => {
          reject(new Error("Failed to load image"))
        }

        img.src = URL.createObjectURL(file)
      })
    }

    // Placeholder for other formats
    throw new Error(`Conversion from ${inputFormat} is not yet implemented`)
  }

  const convertBatch = async (batch: ConvertedFile[], originalFiles: File[]) => {
    const promises = batch.map(async (fileInfo, index) => {
      const originalFile = originalFiles.find((f) => f.name === fileInfo.originalName)
      if (!originalFile) {
        throw new Error(`Original file not found: ${fileInfo.originalName}`)
      }

      try {
        // Update status to converting
        setFiles((prev) => prev.map((f) => (f.id === fileInfo.id ? { ...f, status: "converting" as const } : f)))

        const convertedBlob = await convertSingleFile(originalFile)
        const convertedUrl = URL.createObjectURL(convertedBlob)

        // Update with converted data
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileInfo.id
              ? {
                  ...f,
                  status: "completed" as const,
                  convertedBlob,
                  convertedUrl,
                }
              : f,
          ),
        )

        return { success: true, fileId: fileInfo.id }
      } catch (error) {
        console.error(`Error converting ${fileInfo.originalName}:`, error)

        // Update with error
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileInfo.id
              ? {
                  ...f,
                  status: "error" as const,
                  errorMessage: error instanceof Error ? error.message : "Unknown error",
                }
              : f,
          ),
        )

        return { success: false, fileId: fileInfo.id, error }
      }
    })

    await Promise.all(promises)
  }

  const startBulkConversion = async () => {
    const pendingFiles = files.filter((f) => f.status === "pending")
    if (pendingFiles.length === 0) {
      toast.error("No pending files to convert.")
      return
    }

    // Get original files from the file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement | null
    const originalFiles = Array.from(fileInput?.files || []) as File[]

    setIsConverting(true)
    const batches: ConvertedFile[][] = []

    // Create batches
    for (let i = 0; i < pendingFiles.length; i += BATCH_SIZE) {
      batches.push(pendingFiles.slice(i, i + BATCH_SIZE))
    }

    setTotalBatches(batches.length)
    setCurrentBatch(0)

    const conversionToastId = toast.loading("Starting bulk conversion...")

    try {
      for (let i = 0; i < batches.length; i++) {
        setCurrentBatch(i + 1)
        toast.loading(`Converting batch ${i + 1} of ${batches.length}...`, { id: conversionToastId })

        const currentBatch = batches[i]
        if (currentBatch) {
          await convertBatch(currentBatch, originalFiles)
        }

        // Small delay between batches to prevent browser overload
        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      }

      toast.success("Bulk conversion completed!", { id: conversionToastId })
    } catch (error) {
      console.error("Bulk conversion error:", error)
      toast.error("Some files failed to convert.", { id: conversionToastId })
    } finally {
      setIsConverting(false)
      setCurrentBatch(0)
      setTotalBatches(0)
    }
  }

  const downloadSingleFile = (file: ConvertedFile) => {
    if (file.status !== "completed") return

    const link = document.createElement("a")
    link.href = file.convertedUrl
    link.download = file.originalName.replace(/\.[^/.]+$/, `.${getFileExtension()}`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.success(`Downloaded: ${file.originalName}`)
  }

  const downloadAllFiles = async () => {
    const completedFiles = files.filter((f) => f.status === "completed")

    if (completedFiles.length === 0) {
      toast.error("No converted files to download.")
      return
    }

    const downloadToastId = toast.loading(`Downloading ${completedFiles.length} files...`)

    try {
      // Download files one by one with small delays
      for (const file of completedFiles) {
        downloadSingleFile(file)
        // Small delay to prevent browser from blocking downloads
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      toast.success(`${completedFiles.length} files downloaded!`, { id: downloadToastId })
    } catch (error) {
      toast.error("Error downloading files.", { id: downloadToastId })
    }
  }

  const clearAllFiles = () => {
    // Revoke all object URLs to free memory
    files.forEach((file) => {
      if (file.convertedUrl) {
        URL.revokeObjectURL(file.convertedUrl)
      }
    })

    setFiles([])
    setCurrentBatch(0)
    setTotalBatches(0)

    // Clear file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement | null
    if (fileInput) {
      fileInput.value = ""
    }

    toast.success("All files cleared.")
  }

  const removeFile = (fileId: string) => {
    const fileToRemove = files.find((f) => f.id === fileId)
    if (fileToRemove?.convertedUrl) {
      URL.revokeObjectURL(fileToRemove.convertedUrl)
    }

    setFiles((prev) => prev.filter((f) => f.id !== fileId))
    toast.success("File removed.")
  }

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.convertedUrl) {
          URL.revokeObjectURL(file.convertedUrl)
        }
      })
    }
  }, [])

  const getStatusIcon = (status: ConvertedFile["status"]) => {
    switch (status) {
      case "pending":
        return "⏳"
      case "converting":
        return "🔄"
      case "completed":
        return "✅"
      case "error":
        return "❌"
      default:
        return "⏳"
    }
  }

  const getStatusColor = (status: ConvertedFile["status"]) => {
    switch (status) {
      case "pending":
        return ""
      case "converting":
        return ""
      case "completed":
        return "text-green-600"
      case "error":
        return "text-destructive"
      default:
        return ""
    }
  }

  const completedCount = files.filter((f) => f.status === "completed").length
  const errorCount = files.filter((f) => f.status === "error").length
  const pendingCount = files.filter((f) => f.status === "pending").length

  const selectedInputFormat = INPUT_FORMATS.find((f) => f.value === inputFormat)
  const selectedOutputFormat = OUTPUT_FORMATS.find((f) => f.value === outputFormat)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
          loading: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "#fff",
            },
          },
        }}
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold  mb-2">🖼️ Image Converter</h1>
        <p className="">Convert between different image formats in bulk. Upload up to {MAX_FILES} files at once.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Configuration Panel */}
        <div className="lg:col-span-1">
          <div className="bg-background rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold ">⚙️ Configuration</h2>

            {/* Format Selectors */}
            <div className="space-y-4">
              {/* Input Format */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Input Format</label>
                <Select
                  value={inputFormat}
                  onValueChange={(value) => setInputFormat(value as InputFormat)}
                  disabled={isConverting}
                >
                  <SelectContent>
                    {INPUT_FORMATS.map((format) => (
                      <SelectItem key={format.value} value={format.value} disabled={!format.available}>
                        {format.label} {format.comingSoon ? "(Coming Soon)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Output Format */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  disabled={isConverting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {OUTPUT_FORMATS.map((format) => (
                    <option key={format.value} value={format.value} disabled={!format.available}>
                      {format.label} {format.comingSoon ? "(Coming Soon)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Selection Display */}
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Converting:</span> {selectedInputFormat?.label} →{" "}
                  {selectedOutputFormat?.label}
                </p>
              </div>
            </div>

            {/* Quality & Optimization Controls */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium ">🎛️ Quality & Optimization</h3>

              {/* Optimize Size Toggle */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="optimizeSize"
                  checked={optimizeSize}
                  onChange={(e) => setOptimizeSize(e.target.checked)}
                  disabled={isConverting}
                  className="w-4 h-4  border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <label htmlFor="optimizeSize" className="text-sm text-primary">
                  <span className="font-medium">Optimize file size</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Reduces quality and resizes large images (&gt;2048px) for smaller files
                  </p>
                </label>
              </div>

              {/* Quality Slider (only for JPEG/WebP output) */}
              {outputFormat !== "png" && (
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Quality: {Math.round(quality * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={quality}
                    onChange={(e) => setQuality(parseFloat(e.target.value))}
                    disabled={isConverting}
                    className="w-full h-2 bg-foregroundx rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Smaller files</span>
                    <span>Higher quality</span>
                  </div>
                  {optimizeSize && (
                    <p className="text-xs text-orange-600 mt-1">⚠️ Optimize mode will cap quality at 80% maximum</p>
                  )}
                </div>
              )}

              {/* PNG Note */}
              {outputFormat === "png" && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <span className="font-medium">PNG Format:</span> Lossless compression. Use &quot;Optimize size&quot;
                    to resize large images for smaller files.
                  </p>
                </div>
              )}
            </div>

            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Select {selectedInputFormat?.label} Files (up to {MAX_FILES})
              </label>
              <input
                type="file"
                accept={getFileAcceptString()}
                multiple
                onChange={handleFileUpload}
                disabled={isConverting || !selectedInputFormat?.available}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Select multiple {selectedInputFormat?.label} files. Maximum {MAX_FILES} files.
              </p>
            </div>

            {/* Stats */}
            <div className="bg-foregroundx rounded-lg p-4">
              <h3 className="font-medium  mb-2">Status Overview</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Total Files:</span>
                  <span className="font-medium">{files.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>⏳ Pending:</span>
                  <span className="font-medium">{pendingCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>✅ Completed:</span>
                  <span className="font-medium text-green-600">{completedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>❌ Errors:</span>
                  <span className="font-medium text-destructive">{errorCount}</span>
                </div>
              </div>
            </div>

            {/* Conversion Progress */}
            {isConverting && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Conversion Progress</h3>
                <div className="text-sm text-blue-800">
                  <p>
                    Batch {currentBatch} of {totalBatches}
                  </p>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(currentBatch / totalBatches) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={startBulkConversion}
                disabled={isConverting || pendingCount === 0 || !selectedInputFormat?.available}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isConverting ? "Converting..." : `🔄 Convert ${pendingCount} Files`}
              </button>

              <button
                onClick={downloadAllFiles}
                disabled={completedCount === 0}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                📥 Download All ({completedCount})
              </button>

              <button
                onClick={clearAllFiles}
                disabled={isConverting}
                className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                🗑️ Clear All Files
              </button>
            </div>
          </div>
        </div>

        {/* Files List */}
        <div className="lg:col-span-2">
          <div className="bg-background rounded-lg border p-6">
            <h2 className="text-xl font-semibold  mb-4">📋 Files Queue</h2>

            {files.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="mb-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <p>No files uploaded yet</p>
                <p className="text-sm">Select {selectedInputFormat?.label} files to get started</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-foregroundx rounded-lg">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-lg">{getStatusIcon(file.status)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium  truncate">{file.originalName}</p>
                        <p className="text-xs text-gray-500">{(file.originalSize / 1024 / 1024).toFixed(2)} MB</p>
                        {file.status === "error" && file.errorMessage && (
                          <p className="text-xs text-destructive mt-1">Error: {file.errorMessage}</p>
                        )}
                      </div>
                      <div className={`text-xs font-medium ${getStatusColor(file.status)}`}>
                        {file.status.toUpperCase()}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {file.status === "completed" && (
                        <button
                          onClick={() => downloadSingleFile(file)}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Download
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        disabled={isConverting}
                        className="px-3 py-1 text-xs bg-red-100 text-destructive rounded hover:bg-red-200 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">💡 Tips</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Files are converted in batches of {BATCH_SIZE} to prevent browser overload</li>
              <li>• Use quality slider to balance file size vs image quality</li>
              <li>• &quot;Optimize size&quot; automatically reduces large images and caps quality</li>
              <li>• PNG output is lossless - file size mainly depends on image complexity</li>
              <li>• JPEG/WebP support quality adjustment for compression</li>
              <li>• All processing happens in your browser - files never leave your device</li>
              <li>• Keep the tab active during conversion for best performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageConverter
